import { useState } from "react";
import { ArrowRightLeft, CircleStop, Plus } from "lucide-react";
import type { Contrato } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { useContratos, useEncerrarContrato, type EscopoDeContrato } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { Campo } from "@/componentes/Campo";
import { Botao } from "@/componentes/Botao";
import { Pilula } from "@/componentes/PilulaDeSituacao";
import { useAvisos } from "@/componentes/Avisos";
import { Confirmacao } from "@/componentes/Confirmacao";
import { hojeUTC } from "@/calendario";
import { dia } from "@/formato";
import { franquia, rotuloDaPolitica } from "@/telas/planos/plano";
import { MATIZ_DA_SITUACAO } from "@/telas/planos/situacao";
import { NovoContrato } from "./NovoContrato";
import { TrocarPlano } from "./TrocarPlano";

/** Franquia e política do plano VIGENTE, que a lista de contratos não traz. */
export interface ResumoDoPlano {
  limite_mensal: number | null;
  politica: string;
}

/*
 * A aba Contrato do detalhe da aplicação — e o contrato de cada cedente, na aba
 * Cedentes (change `add-cobranca-por-cedente`, D7).
 *
 * Um componente para os dois escopos: as regras de vigência, troca e
 * encerramento são as mesmas, e duas telas escritas à parte divergiriam na
 * primeira correção. `nome` é quem perde a emissão no encerramento — a
 * aplicação ou a clínica —, e é ele que a confirmação manda digitar.
 *
 * O vigente em destaque, o histórico embaixo. "Vigente" é o que o servidor diz
 * (`situacao`, derivada no calendário UTC) — a tela não compara datas com o
 * relógio do navegador, que em Brasília vira o dia três horas depois.
 */
export function AbaDeContrato({
  escopo,
  nome,
  plano,
  ativa,
}: {
  escopo: EscopoDeContrato;
  nome: string;
  /** `null` sem contrato vigente: aí o nulo do limite é ausência, e não "sem limite". */
  plano: ResumoDoPlano | null;
  ativa: boolean;
}) {
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();

  const { data, error, isPending } = useContratos(cliente, escopo, ativa);
  const contratos = data?.contratos ?? [];
  const vigente = contratos.find((contrato) => contrato.vigente) ?? null;
  const historico = contratos.filter((contrato) => contrato !== vigente);

  const [modal, setModal] = useState<"novo" | "troca" | null>(null);
  const [encerrando, setEncerrando] = useState<Contrato | null>(null);
  const [fim, setFim] = useState("");
  const [imediato, setImediato] = useState(false);
  const encerrar = useEncerrarContrato(cliente, escopo);

  const ehAdmin = pode("admin");

  function abrirEncerramento(contrato: Contrato) {
    setFim(hojeUTC());
    // Contrato FUTURO só se cancela de imediato. Com data, o servidor grava
    // `fim` ≥ `inicio` e mantém `ativo` — e o contrato que o operador quis
    // cancelar ainda vigoraria no primeiro dia dele.
    setImediato(contrato.situacao === "futuro");
    setEncerrando(contrato);
  }

  if (error) {
    return (
      <p role="alert" className="text-falha">
        {textoDoErro(error)}
      </p>
    );
  }

  const cancelandoFuturo = encerrando?.situacao === "futuro";

  return (
    <div className="flex flex-col gap-4">
      {vigente !== null ? (
        <div className="grid grid-cols-2 gap-2">
          <CaixaDeCampo rotulo="Plano" className="col-span-2">
            {vigente.plano_nome}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Franquia" dados>
            {/*
              `limite_mensal` nulo com contrato vigente é plano SEM LIMITE; sem
              contrato vigente, o nulo é só ausência — por isso a leitura
              depende de `vigente`.
            */}
            {plano !== null ? franquia(plano.limite_mensal) : null}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Política">
            {plano !== null ? rotuloDaPolitica(plano.politica) : null}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Início" dados>
            {dia(vigente.inicio)}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Fim" dados>
            {vigente.fim === null ? "aberto" : dia(vigente.fim)}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Vencimento da fatura" dados>
            {`dia ${vigente.dia_vencimento}`}
          </CaixaDeCampo>
        </div>
      ) : (
        !isPending && (
          // Sem contrato vigente não há emissão: é falha, não neutro.
          <p className="text-falha">Sem contrato vigente.</p>
        )
      )}

      {ehAdmin && !isPending && (
        <div className="flex flex-wrap gap-2">
          {vigente === null ? (
            <Botao onClick={() => setModal("novo")}>
              <Plus aria-hidden="true" className="size-4" />
              Novo contrato
            </Botao>
          ) : (
            <>
              <Botao tom="secundario" onClick={() => setModal("troca")}>
                <ArrowRightLeft aria-hidden="true" className="size-4" />
                Trocar de plano
              </Botao>
              <Botao tom="destrutivo" onClick={() => abrirEncerramento(vigente)}>
                <CircleStop aria-hidden="true" className="size-4" />
                Encerrar
              </Botao>
            </>
          )}
        </div>
      )}

      {historico.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="rotulo">Histórico</h3>
          <ul className="flex flex-col gap-1">
            {historico.map((contrato) => (
              <li
                key={contrato.id}
                className="flex items-center gap-2 rounded-controle border border-fio bg-superficie-2 px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate text-texto">{contrato.plano_nome}</span>
                <span className="dados shrink-0 text-[12px] text-texto-2">
                  {dia(contrato.inicio)} → {contrato.fim === null ? "aberto" : dia(contrato.fim)}
                </span>
                <Pilula matiz={MATIZ_DA_SITUACAO[contrato.situacao]}>{contrato.situacao}</Pilula>
                {/*
                  Contrato futuro se cancela antes de valer. NÃO desfaz a troca
                  que o criou: o contrato atual continua terminando na véspera
                  (o encerramento não estende vigência), e quem quiser seguir
                  emitindo depois troca de plano de novo a partir daquele dia.
                */}
                {ehAdmin && contrato.situacao === "futuro" && (
                  <button
                    type="button"
                    aria-label={`Cancelar o contrato futuro ${contrato.plano_nome}`}
                    title="Cancelar"
                    onClick={() => abrirEncerramento(contrato)}
                    className="rounded-controle p-1 text-texto-3 hover:bg-fio hover:text-falha"
                  >
                    <CircleStop aria-hidden="true" className="size-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {modal === "novo" && (
        <NovoContrato escopo={escopo} nome={nome} aoFechar={() => setModal(null)} />
      )}

      {modal === "troca" && vigente !== null && (
        <TrocarPlano
          escopo={escopo}
          nome={nome}
          vigente={vigente}
          aoFechar={() => setModal(null)}
        />
      )}

      {/*
        ┌──────────────────────────────────────────────────────────────────────┐
        │ CONFIRMAÇÃO CRÍTICA, COM O NOME DE QUEM PERDE A EMISSÃO DIGITADO.    │
        │                                                                      │
        │ Encerrar derruba a emissão da aplicação (ou da clínica, no contrato  │
        │ do par) — com `imediato`, no mesmo instante (fraude, inadimplência). │
        │ O atrito separa "cliquei na linha errada" de "decidi parar este      │
        │ cliente".                                                            │
        │                                                                      │
        │ O pedido é montado a cada renderização, e não guardado em estado:    │
        │ a data e o "agora" mudam enquanto o diálogo está aberto, e o corpo   │
        │ precisa refletir o valor atual. A `key` do diálogo (título + verbo)  │
        │ não muda com eles, então o que já foi digitado não se perde.         │
        └──────────────────────────────────────────────────────────────────────┘
      */}
      <Confirmacao
        pedido={
          encerrando === null
            ? null
            : {
                severidade: "critica",
                titulo: cancelandoFuturo
                  ? `Cancelar o contrato futuro de ${nome}?`
                  : `Encerrar o contrato de ${nome}?`,
                verbo: cancelandoFuturo ? "Cancelar contrato" : "Encerrar contrato",
                digitarParaLiberar: nome,
                // O cancelamento do futuro não devolve ao atual a vigência que
                // a troca encurtou: a consequência que importa é a data em que
                // a emissão para, e é ela que a confirmação diz.
                corpo: cancelandoFuturo ? (
                  <p>
                    {vigente !== null && vigente.fim !== null
                      ? `Não chega a vigorar; o atual continua terminando em ${dia(vigente.fim)}.`
                      : "Não chega a vigorar."}
                  </p>
                ) : (
                  <div className="flex flex-col gap-3">
                    <p>{imediato ? "Encerra agora." : `Vigência até ${dia(fim)}.`}</p>
                    <div className="flex items-end gap-3">
                      <Campo
                        rotulo="Último dia"
                        type="date"
                        value={fim}
                        min={hojeUTC()}
                        disabled={imediato}
                        onChange={(evento) => setFim(evento.target.value)}
                      />
                      <label className="flex h-9 items-center gap-2 text-corpo text-texto">
                        <input
                          type="checkbox"
                          checked={imediato}
                          onChange={(evento) => setImediato(evento.target.checked)}
                        />
                        Agora
                      </label>
                    </div>
                  </div>
                ),
              }
        }
        aoCancelar={() => setEncerrando(null)}
        aoConfirmar={async () => {
          if (encerrando === null) return;
          await encerrar.mutateAsync({
            contratoID: encerrando.id,
            corpo: imediato || cancelandoFuturo ? { imediato: true } : { fim, imediato: false },
          });
          setEncerrando(null);
          avisos.feito(cancelandoFuturo ? "Contrato futuro cancelado" : "Contrato encerrado");
        }}
      />
    </div>
  );
}
